import { useEffect, useState } from "react";
import styles from "./CreateNotificationPage.module.css";
import { ENDPOINTS } from "../../utils/endpoints";
import api from "../../services/api";
import { toast } from "react-toastify";
import useDashboardStore from "../../store/dashboardStore";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

const AUDIENCE_OPTIONS = [
  { value: "all", label: "Everyone" },
  { value: "all_users", label: "All Users (subscribers)" },
  { value: "all_traders", label: "All Traders" },
  { value: "free", label: "Free Users" },
  { value: "paid", label: "Paid Users" },
  { value: "per_plan", label: "Specific Plan(s)" },
  { value: "per_user", label: "Specific User(s)" },
  { value: "per_trader", label: "Specific Trader(s)" },
  { value: "by_expiry", label: "Expiring / Expired Users" },
];

const EXPIRY_PRESETS = [1, 7, 14, 30];

const REPEAT_OPTIONS = [
  { value: "once", label: "Send once" },
  { value: "hourly", label: "Hourly" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const WEEKDAYS = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

// The browser's IANA timezone (e.g. "Asia/Beirut"). The chosen send time is
// interpreted in this zone, then converted to a UTC instant on the backend.
const BROWSER_TZ =
  Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

const CreateNotificationPage = () => {
  const { setIsAllNotificationsScreen, editSchedule, setEditSchedule } =
    useDashboardStore();

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [subBody, setSubBody] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  const [audienceType, setAudienceType] = useState("all");
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedTraders, setSelectedTraders] = useState([]);

  // ─── Recurring schedule ───────────────────────────────────────────────────
  const [repeatMode, setRepeatMode] = useState("once"); // once|hourly|daily|weekly|monthly
  const [intervalValue, setIntervalValue] = useState(1);
  const [daysOfWeek, setDaysOfWeek] = useState([]);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [sendTime, setSendTime] = useState("09:00");
  const [endMode, setEndMode] = useState("never"); // never|count
  const [maxOccurrences, setMaxOccurrences] = useState(2);
  const isSchedule = repeatMode !== "once";

  // Prefill when editing an existing schedule (set via the Scheduled list).
  useEffect(() => {
    if (!editSchedule) return;
    const s = editSchedule;
    setTitle(s.title || "");
    setBody(s.body || "");
    setSubBody(s.sub_body || "");
    setLinkUrl(s.link_url || "");
    setAudienceType(s.audience_type || "all");
    if (Array.isArray(s.audience_plan_ids)) setSelectedPlans(s.audience_plan_ids);
    if (s.audience_type === "per_user" && Array.isArray(s.audience_user_ids))
      setSelectedUsers(s.audience_user_ids);
    if (s.audience_type === "per_trader" && Array.isArray(s.audience_user_ids))
      setSelectedTraders(s.audience_user_ids);
    setRepeatMode(s.repeat_type || "daily");
    setIntervalValue(s.interval_value || 1);
    if (Array.isArray(s.days_of_week)) setDaysOfWeek(s.days_of_week);
    if (s.day_of_month) setDayOfMonth(s.day_of_month);
    if (s.send_time) setSendTime(s.send_time);
    setEndMode(s.end_type || "never");
    if (s.max_occurrences) setMaxOccurrences(s.max_occurrences);
  }, [editSchedule]);

  // Clear the edit context when leaving the page.
  useEffect(() => {
    return () => setEditSchedule(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleDayOfWeek = (d) =>
    setDaysOfWeek((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d],
    );

  const [memberships, setMemberships] = useState([]);
  const [users, setUsers] = useState([]);
  const [traders, setTraders] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [traderSearch, setTraderSearch] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [expiryMode, setExpiryMode] = useState("expiring");
  const [expiryDays, setExpiryDays] = useState(7);
  const [expiryUsers, setExpiryUsers] = useState([]);
  const [selectedExpiryUsers, setSelectedExpiryUsers] = useState([]);
  const [expirySearch, setExpirySearch] = useState("");
  const [isLoadingExpiry, setIsLoadingExpiry] = useState(false);

  const filteredUsers = users.filter((u) => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      String(u.id ?? "").toLowerCase().includes(q) ||
      (u.username || "").toLowerCase().includes(q) ||
      (u.display_name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q)
    );
  });

  const filteredExpiryUsers = expiryUsers.filter((u) => {
    const q = expirySearch.trim().toLowerCase();
    if (!q) return true;
    return (
      String(u.id ?? "").toLowerCase().includes(q) ||
      (u.username || "").toLowerCase().includes(q) ||
      (u.display_name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q)
    );
  });

  const fetchExpiryUsers = async () => {
    try {
      setIsLoadingExpiry(true);
      const { data } = await api.get(
        ENDPOINTS.GET_USERS_BY_EXPIRY(expiryMode, expiryDays),
      );
      if (data.success) {
        const list = data.users || [];
        setExpiryUsers(list);
        setSelectedExpiryUsers([]);
      }
    } catch (err) {
      console.error("Failed to load expiring users", err);
      toast.error("Failed to load users");
    } finally {
      setIsLoadingExpiry(false);
    }
  };

  useEffect(() => {
    if (audienceType === "by_expiry" && typeof expiryDays === "number" && expiryDays >= 1) {
      fetchExpiryUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceType, expiryMode, expiryDays]);

  const toggleExpiryUser = (id) =>
    setSelectedExpiryUsers((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const filteredTraders = traders.filter((t) => {
    const q = traderSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      String(t.id ?? "").toLowerCase().includes(q) ||
      (t.username || "").toLowerCase().includes(q) ||
      (t.display_name || "").toLowerCase().includes(q)
    );
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(ENDPOINTS.GET_ALL_MEMBERSHIPS);
        if (data.success) setMemberships(data.memberships || []);
      } catch (err) {
        console.error("Failed to load memberships", err);
      }
    })();
    (async () => {
      try {
        const { data } = await api.get(ENDPOINTS.GET_ALL_USERS_SIMPLE);
        if (data.success) setUsers(data.users || []);
      } catch (err) {
        console.error("Failed to load users", err);
      }
    })();
    (async () => {
      try {
        const { data } = await api.get(ENDPOINTS.GET_ALL_TRADERS_SIMPLE);
        if (data.success) setTraders(data.traders || []);
      } catch (err) {
        console.error("Failed to load traders", err);
      }
    })();
  }, []);

  const togglePlan = (id) =>
    setSelectedPlans((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  const toggleUser = (id) =>
    setSelectedUsers((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  const toggleTrader = (id) =>
    setSelectedTraders((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    if (audienceType === "per_plan" && selectedPlans.length === 0) {
      toast.error("Pick at least one plan");
      return;
    }
    if (audienceType === "per_user" && selectedUsers.length === 0) {
      toast.error("Pick at least one user");
      return;
    }
    if (audienceType === "per_trader" && selectedTraders.length === 0) {
      toast.error("Pick at least one trader");
      return;
    }
    if (audienceType === "by_expiry" && selectedExpiryUsers.length === 0) {
      toast.error("Pick at least one user");
      return;
    }

    if (isSchedule && repeatMode === "weekly" && daysOfWeek.length === 0) {
      toast.error("Pick at least one weekday");
      return;
    }
    if (isSchedule && endMode === "count") {
      const n = parseInt(maxOccurrences, 10);
      if (!n || n < 1) {
        toast.error("Enter how many times to repeat (>= 1)");
        return;
      }
    }

    const audience_user_ids =
      audienceType === "per_user"
        ? selectedUsers
        : audienceType === "per_trader"
        ? selectedTraders
        : audienceType === "by_expiry"
        ? selectedExpiryUsers
        : null;

    const finalAudienceType =
      audienceType === "by_expiry" ? "per_user" : audienceType;

    const audience_plan_ids =
      audienceType === "per_plan" ? selectedPlans : null;

    if (isSchedule) {
      const schedulePayload = {
        title: title.trim(),
        body: body.trim(),
        sub_body: subBody.trim() || null,
        link_url: linkUrl.trim() || null,
        link_type: linkUrl.trim() ? "external" : "none",
        audience_type: finalAudienceType,
        audience_plan_ids,
        audience_user_ids,
        repeat_type: repeatMode,
        interval_value: Math.max(1, parseInt(intervalValue, 10) || 1),
        days_of_week: repeatMode === "weekly" ? daysOfWeek : null,
        day_of_month:
          repeatMode === "monthly"
            ? Math.min(31, Math.max(1, parseInt(dayOfMonth, 10) || 1))
            : null,
        send_time: repeatMode === "hourly" ? null : sendTime,
        timezone: BROWSER_TZ,
        end_type: endMode,
        max_occurrences:
          endMode === "count" ? parseInt(maxOccurrences, 10) : null,
      };

      try {
        setIsSubmitting(true);
        const { data } = editSchedule
          ? await api.patch(
              ENDPOINTS.UPDATE_NOTIFICATION_SCHEDULE(editSchedule.id),
              schedulePayload,
            )
          : await api.post(
              ENDPOINTS.CREATE_NOTIFICATION_SCHEDULE,
              schedulePayload,
            );
        if (data.success) {
          toast.success(editSchedule ? "Schedule updated" : "Schedule created");
          setEditSchedule(null);
          setIsAllNotificationsScreen();
        } else {
          toast.error(data.message || "Failed to save schedule");
        }
      } catch (err) {
        console.error(err);
        toast.error(err.response?.data?.message || "Failed to save schedule");
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    const payload = {
      title: title.trim(),
      body: body.trim(),
      sub_body: subBody.trim() || null,
      image_url: null,
      link_url: linkUrl.trim() || null,
      link_type: linkUrl.trim() ? "external" : "none",
      audience_type: finalAudienceType,
      audience_plan_ids,
      audience_user_ids,
      expires_at: expiresAt
        ? dayjs(expiresAt).utc().format("YYYY-MM-DDTHH:mm:ss")
        : null,
    };

    try {
      setIsSubmitting(true);
      const { data } = await api.post(ENDPOINTS.SEND_NOTIFICATION, payload);
      if (data.success) {
        toast.success(
          `Sent to ${data.recipient_count} recipient${
            data.recipient_count === 1 ? "" : "s"
          }`,
        );
        setIsAllNotificationsScreen();
      } else {
        toast.error(data.message || "Failed to send");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to send notification");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.appbarContainer}>
        <h2 className={styles.appbarTitle}>
          {editSchedule
            ? "Edit Schedule"
            : isSchedule
            ? "New Schedule"
            : "Send Notification"}
        </h2>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => setIsAllNotificationsScreen()}
        >
          Back
        </button>
      </div>

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.field}>
          <label className={styles.label}>Title *</label>
          <input
            type="text"
            className={styles.input}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={255}
            placeholder="e.g. New trading session tonight"
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Body *</label>
          <textarea
            className={styles.textarea}
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Message users will see"
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Sub Body (optional)</label>
          <textarea
            className={styles.textarea}
            rows={6}
            value={subBody}
            onChange={(e) => setSubBody(e.target.value)}
            placeholder="Longer details shown when the user taps “Show more”"
          />
          <small className={styles.hint}>
            Only shown inside the app and website (when the user expands the
            notification). It is not part of the push notification.
          </small>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Link URL (optional)</label>
          <input
            type="text"
            className={styles.input}
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://tiktok.com/... or any link, including an image URL"
          />
          <small className={styles.hint}>
            Opens in a new tab on web. Deep-links to the app on mobile
            (YouTube, Instagram, TikTok, WhatsApp, Facebook) if installed,
            otherwise falls back to the browser. You can also paste a direct
            image URL — it will open in the browser as an image preview.
          </small>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Expires At (optional)</label>
          <input
            type="datetime-local"
            className={styles.input}
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
          <small className={styles.hint}>
            After this date, users won't see it in their notification center.
          </small>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Audience *</label>
          <div className={styles.audienceGrid}>
            {AUDIENCE_OPTIONS.map((o) => (
              <label
                key={o.value}
                className={`${styles.audienceItem} ${
                  audienceType === o.value ? styles.audienceItemActive : ""
                }`}
              >
                <input
                  type="radio"
                  name="audience"
                  value={o.value}
                  checked={audienceType === o.value}
                  onChange={(e) => setAudienceType(e.target.value)}
                />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
        </div>

        {audienceType === "per_plan" && (
          <div className={styles.field}>
            <label className={styles.label}>
              Pick plans ({selectedPlans.length} selected)
            </label>
            <div className={styles.pickerList}>
              {memberships.map((m) => (
                <label key={m.id} className={styles.pickerItem}>
                  <input
                    type="checkbox"
                    checked={selectedPlans.includes(m.id)}
                    onChange={() => togglePlan(m.id)}
                  />
                  <span>{m.name || m.title || `Plan ${m.id}`}</span>
                </label>
              ))}
              {memberships.length === 0 && (
                <small className={styles.hint}>No plans available.</small>
              )}
            </div>
          </div>
        )}

        {audienceType === "per_user" && (
          <div className={styles.field}>
            <label className={styles.label}>
              Pick users ({selectedUsers.length} selected)
            </label>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search by ID, username or name..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
            <div className={styles.pickerList}>
              {filteredUsers.map((u) => (
                <label key={u.id} className={styles.pickerItem}>
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(u.id)}
                    onChange={() => toggleUser(u.id)}
                  />
                  <span>
                    {u.username || u.display_name || `User ${u.id}`}
                    <span style={{ color: "#888", marginLeft: 6 }}>
                      #{u.id}
                    </span>
                  </span>
                </label>
              ))}
              {filteredUsers.length === 0 && (
                <small className={styles.hint}>
                  {users.length === 0 ? "No users available." : "No matches."}
                </small>
              )}
            </div>
          </div>
        )}

        {audienceType === "by_expiry" && (
          <div className={styles.field}>
            <label className={styles.label}>Expiry window</label>
            <div
              className={styles.audienceGrid}
              style={{ marginBottom: 8 }}
            >
              <label
                className={`${styles.audienceItem} ${
                  expiryMode === "expiring" ? styles.audienceItemActive : ""
                }`}
              >
                <input
                  type="radio"
                  name="expiryMode"
                  value="expiring"
                  checked={expiryMode === "expiring"}
                  onChange={(e) => setExpiryMode(e.target.value)}
                />
                <span>Will expire</span>
              </label>
              <label
                className={`${styles.audienceItem} ${
                  expiryMode === "expired" ? styles.audienceItemActive : ""
                }`}
              >
                <input
                  type="radio"
                  name="expiryMode"
                  value="expired"
                  checked={expiryMode === "expired"}
                  onChange={(e) => setExpiryMode(e.target.value)}
                />
                <span>Already expired</span>
              </label>
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
              {EXPIRY_PRESETS.map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`${styles.audienceItem} ${
                    expiryDays === d ? styles.audienceItemActive : ""
                  }`}
                  style={{ cursor: "pointer" }}
                  onClick={() => setExpiryDays(d)}
                >
                  {d} day{d === 1 ? "" : "s"}
                </button>
              ))}
              <input
                type="number"
                min={1}
                max={365}
                className={styles.input}
                style={{ width: 100 }}
                value={expiryDays}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "") {
                    setExpiryDays("");
                  } else {
                    const n = parseInt(v, 10);
                    if (!isNaN(n)) setExpiryDays(n);
                  }
                }}
                onBlur={() => {
                  const n = parseInt(expiryDays, 10);
                  setExpiryDays(isNaN(n) ? 1 : Math.max(1, Math.min(365, n)));
                }}
              />
              <small className={styles.hint}>
                {expiryMode === "expired"
                  ? `Users whose membership expired within the last ${expiryDays} day(s)`
                  : `Users whose membership will expire in the next ${expiryDays} day(s)`}
              </small>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <label className={styles.label} style={{ marginBottom: 0 }}>
                Matched users ({selectedExpiryUsers.length}/{expiryUsers.length} selected)
              </label>
              {filteredExpiryUsers.length > 0 && (
                <button
                  type="button"
                  className={styles.audienceItem}
                  style={{ cursor: "pointer", fontSize: 12 }}
                  onClick={() => {
                    const visibleIds = filteredExpiryUsers.map((u) => u.id);
                    const allSelected = visibleIds.every((id) =>
                      selectedExpiryUsers.includes(id),
                    );
                    if (allSelected) {
                      setSelectedExpiryUsers((prev) =>
                        prev.filter((id) => !visibleIds.includes(id)),
                      );
                    } else {
                      setSelectedExpiryUsers((prev) =>
                        Array.from(new Set([...prev, ...visibleIds])),
                      );
                    }
                  }}
                >
                  {filteredExpiryUsers.every((u) =>
                    selectedExpiryUsers.includes(u.id),
                  )
                    ? "Unselect all"
                    : "Select all"}
                </button>
              )}
            </div>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search by ID, username, name or email..."
              value={expirySearch}
              onChange={(e) => setExpirySearch(e.target.value)}
            />
            <div className={styles.pickerList}>
              {isLoadingExpiry ? (
                <small className={styles.hint}>Loading users...</small>
              ) : filteredExpiryUsers.length === 0 ? (
                <small className={styles.hint}>
                  {expiryUsers.length === 0
                    ? "No users match this window."
                    : "No matches."}
                </small>
              ) : (
                filteredExpiryUsers.map((u) => (
                  <label key={u.id} className={styles.pickerItem}>
                    <input
                      type="checkbox"
                      checked={selectedExpiryUsers.includes(u.id)}
                      onChange={() => toggleExpiryUser(u.id)}
                    />
                    <span>
                      {u.display_name || u.username || `User ${u.id}`}
                      <span style={{ color: "#888", marginLeft: 6 }}>
                        #{u.id}
                      </span>
                      {u.membership_name && (
                        <span style={{ color: "#666", marginLeft: 8, fontSize: 12 }}>
                          · {u.membership_name}
                        </span>
                      )}
                      {u.expire_time && (
                        <span style={{ color: "#888", marginLeft: 8, fontSize: 12 }}>
                          · expires {dayjs.utc(u.expire_time).local().format("DD-MM-YYYY")}
                        </span>
                      )}
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>
        )}

        {audienceType === "per_trader" && (
          <div className={styles.field}>
            <label className={styles.label}>
              Pick traders ({selectedTraders.length} selected)
            </label>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search by ID, username or name..."
              value={traderSearch}
              onChange={(e) => setTraderSearch(e.target.value)}
            />
            <div className={styles.pickerList}>
              {filteredTraders.map((t) => (
                <label key={t.id} className={styles.pickerItem}>
                  <input
                    type="checkbox"
                    checked={selectedTraders.includes(t.id)}
                    onChange={() => toggleTrader(t.id)}
                  />
                  <span>
                    {t.username || t.display_name || `Trader ${t.id}`}
                    <span style={{ color: "#888", marginLeft: 6 }}>
                      #{t.id}
                    </span>
                  </span>
                </label>
              ))}
              {filteredTraders.length === 0 && (
                <small className={styles.hint}>
                  {traders.length === 0
                    ? "No traders available."
                    : "No matches."}
                </small>
              )}
            </div>
          </div>
        )}

        <div className={styles.field}>
          <label className={styles.label}>Repeat</label>
          <div className={styles.audienceGrid}>
            {REPEAT_OPTIONS.map((o) => (
              <label
                key={o.value}
                className={`${styles.audienceItem} ${
                  repeatMode === o.value ? styles.audienceItemActive : ""
                }`}
              >
                <input
                  type="radio"
                  name="repeat"
                  value={o.value}
                  checked={repeatMode === o.value}
                  onChange={(e) => setRepeatMode(e.target.value)}
                />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
        </div>

        {isSchedule && (
          <>
            {(repeatMode === "hourly" || repeatMode === "daily") && (
              <div className={styles.field}>
                <label className={styles.label}>
                  Every (in {repeatMode === "hourly" ? "hours" : "days"})
                </label>
                <input
                  type="number"
                  min={1}
                  className={styles.input}
                  style={{ width: 120 }}
                  value={intervalValue}
                  onChange={(e) => setIntervalValue(e.target.value)}
                />
              </div>
            )}

            {repeatMode === "weekly" && (
              <div className={styles.field}>
                <label className={styles.label}>On days</label>
                <div className={styles.audienceGrid}>
                  {WEEKDAYS.map((d) => (
                    <label
                      key={d.value}
                      className={`${styles.audienceItem} ${
                        daysOfWeek.includes(d.value)
                          ? styles.audienceItemActive
                          : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={daysOfWeek.includes(d.value)}
                        onChange={() => toggleDayOfWeek(d.value)}
                      />
                      <span>{d.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {repeatMode === "monthly" && (
              <div className={styles.field}>
                <label className={styles.label}>Day of month (1-31)</label>
                <input
                  type="number"
                  min={1}
                  max={31}
                  className={styles.input}
                  style={{ width: 120 }}
                  value={dayOfMonth}
                  onChange={(e) => setDayOfMonth(e.target.value)}
                />
                <small className={styles.hint}>
                  29–31 fall back to the last day in shorter months (e.g. 31 → Feb
                  28/29, Apr 30).
                </small>
              </div>
            )}

            {repeatMode !== "hourly" && (
              <div className={styles.field}>
                <label className={styles.label}>Time</label>
                <input
                  type="time"
                  className={styles.input}
                  style={{ width: 160 }}
                  value={sendTime}
                  onChange={(e) => setSendTime(e.target.value)}
                />
                <small className={styles.hint}>
                  Sent at this time in your timezone ({BROWSER_TZ}). Everyone
                  receives it at the same moment (their matching local time).
                </small>
              </div>
            )}

            <div className={styles.field}>
              <label className={styles.label}>Ends</label>
              <div className={styles.audienceGrid} style={{ marginBottom: 8 }}>
                <label
                  className={`${styles.audienceItem} ${
                    endMode === "never" ? styles.audienceItemActive : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="endMode"
                    value="never"
                    checked={endMode === "never"}
                    onChange={(e) => setEndMode(e.target.value)}
                  />
                  <span>Forever</span>
                </label>
                <label
                  className={`${styles.audienceItem} ${
                    endMode === "count" ? styles.audienceItemActive : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="endMode"
                    value="count"
                    checked={endMode === "count"}
                    onChange={(e) => setEndMode(e.target.value)}
                  />
                  <span>After N times</span>
                </label>
              </div>
              {endMode === "count" && (
                <input
                  type="number"
                  min={1}
                  className={styles.input}
                  style={{ width: 120 }}
                  value={maxOccurrences}
                  onChange={(e) => setMaxOccurrences(e.target.value)}
                />
              )}
            </div>
          </>
        )}

        <div className={styles.actions}>
          <button
            type="submit"
            className={styles.submitButton}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Saving..."
              : !isSchedule
              ? "Send Notification"
              : editSchedule
              ? "Update Schedule"
              : "Create Schedule"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateNotificationPage;
